import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import AdminLayout from "../../components/AdminLayout";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./Bookings.css";

const API_BASE = process.env.REACT_APP_API_URL;
const ORG_ID = "resh-dev";
const PAGE_SIZE = Number(process.env.REACT_APP_PAGE_SIZE) || 25;

function authHeaders() {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
    OrgId: ORG_ID,
  };
}

// Helper to parse MongoDB Extended JSON dates and standard strings
const parseMongoDate = (dateField) => {
  if (!dateField) return null;
  if (typeof dateField === "string") return new Date(dateField);
  if (dateField.$date) {
    if (typeof dateField.$date === "string") return new Date(dateField.$date);
    if (typeof dateField.$date === "number") return new Date(dateField.$date);
  }
  return null;
};

// Custom dropdown filter component
function FilterDropdown({ label, options, value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div className="booking-dropdown" ref={ref}>
      <button
        type="button"
        className={`booking-dropdown__trigger ${isOpen ? "booking-dropdown__trigger--open" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{selectedOption ? selectedOption.label : label}</span>
        <svg
          className="booking-dropdown__chevron"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          width="14"
          height="14"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <ul className="booking-dropdown__menu">
          {options.map((opt) => (
            <li
              key={opt.value}
              className={`booking-dropdown__item ${value === opt.value ? "booking-dropdown__item--active" : ""}`}
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
            >
              <span>{opt.label}</span>
              {value === opt.value && (
                <svg
                  className="booking-dropdown__check"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  width="12"
                  height="12"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Row-level action menu with Status and Payment status updates
function ActionDropdown({ booking, paymentStatus, onTriggerStatus, onUpdatePayment, onViewDetails }) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef();
  const menuRef = useRef();

  useEffect(() => {
    const handleClick = (e) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    const handleScroll = () => setIsOpen(false);
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  const openMenu = () => {
    const rect = btnRef.current.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + 6,
      left: rect.right - 180,
    });
    setIsOpen(true);
  };

  const isFinalState = booking.status === "Cancelled" || booking.status === "Completed";
  const showPaymentChange = booking.status === "Confirmed" && paymentStatus !== "Fully Paid";

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="booking-btn booking-btn--icon-only"
        onClick={openMenu}
        title="More Actions"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
          <circle cx="12" cy="12" r="1.5" />
          <circle cx="12" cy="5" r="1.5" />
          <circle cx="12" cy="19" r="1.5" />
        </svg>
      </button>

      {isOpen && createPortal(
        <div
          ref={menuRef}
          className="action-dropdown__menu"
          style={{ top: menuPos.top, left: menuPos.left }}
        >
          <button className="action-dropdown__item" onClick={() => { onViewDetails(); setIsOpen(false); }}>
            <span>View Details</span>
          </button>

          {!isFinalState && (
            <>
              <div className="action-dropdown__divider" />
              <div className="action-dropdown__header">Update Status</div>
              {booking.status !== "Confirmed" && (
                <button className="action-dropdown__item" onClick={() => { onTriggerStatus(booking._id, "confirm"); setIsOpen(false); }}><span>Confirm</span></button>
              )}
              {booking.status === "Confirmed" && (
                <button className="action-dropdown__item" onClick={() => { onTriggerStatus(booking._id, "complete"); setIsOpen(false); }}><span>Complete</span></button>
              )}
              {booking.status !== "Cancelled" && (
                <button className="action-dropdown__item" onClick={() => { onTriggerStatus(booking._id, "cancel"); setIsOpen(false); }}><span>Cancel</span></button>
              )}
            </>
          )}

          {showPaymentChange && (
            <>
              <div className="action-dropdown__divider" />
              <div className="action-dropdown__header">Payment Status</div>
              {(
                paymentStatus === "Fully Paid" ? ["Fully Paid"] :
                  paymentStatus === "Advance Paid" ? ["Advance Paid", "Fully Paid"] :
                    ["Advance Paid", "Fully Paid"]
              ).map((pay) => (
                <button
                  key={pay}
                  className={`action-dropdown__item ${paymentStatus === pay ? "action-dropdown__item--active" : ""}`}
                  onClick={() => { onUpdatePayment(booking._id, pay); setIsOpen(false); }}
                >
                  <span>{pay}</span>
                </button>
              ))}
            </>
          )}
        </div>,
        document.body
      )}
    </>
  );
}

// ── Pagination Controls ──────────────────────────────────────────────────────
// Only renders when totalDocs exceeds one page (i.e. more than PAGE_SIZE records)
function PaginationControls({ currentPage, totalPages, totalDocs, pageSize, onPageChange, loading }) {
  // Hide pagination entirely when all results fit on one page
  if (!loading && totalDocs <= pageSize) return null;

  const startDoc = totalDocs > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endDoc = Math.min(currentPage * pageSize, totalDocs);

  // Build page numbers window of 5
  const pages = [];
  const delta = 2;
  const left = Math.max(1, currentPage - delta);
  const right = Math.min(Math.max(totalPages, 1), currentPage + delta);
  for (let i = left; i <= right; i++) pages.push(i);

  const isDisabled = loading || totalPages <= 1;

  return (
    <div className={`bookings-pagination${loading ? " bookings-pagination--loading" : ""}`}>
      <span className="bookings-pagination__info">
        {loading
          ? "Loading…"
          : totalDocs === 0
            ? "No results"
            : `Showing ${startDoc}–${endDoc} of ${totalDocs} bookings`}
      </span>
      <div className="bookings-pagination__controls">
        <button
          className="bookings-pagination__btn"
          disabled={currentPage === 1 || isDisabled}
          onClick={() => onPageChange(1)}
          title="First page"
        >
          «
        </button>
        <button
          className="bookings-pagination__btn"
          disabled={currentPage === 1 || isDisabled}
          onClick={() => onPageChange(currentPage - 1)}
          title="Previous page"
        >
          ‹
        </button>

        {left > 1 && (
          <>
            <button className="bookings-pagination__btn" disabled={isDisabled} onClick={() => onPageChange(1)}>1</button>
            {left > 2 && <span className="bookings-pagination__ellipsis">…</span>}
          </>
        )}

        {pages.map((p) => (
          <button
            key={p}
            className={`bookings-pagination__btn ${p === currentPage ? "bookings-pagination__btn--active" : ""}`}
            disabled={loading}
            onClick={() => onPageChange(p)}
          >
            {p}
          </button>
        ))}

        {right < totalPages && (
          <>
            {right < totalPages - 1 && <span className="bookings-pagination__ellipsis">…</span>}
            <button className="bookings-pagination__btn" disabled={isDisabled} onClick={() => onPageChange(totalPages)}>{totalPages}</button>
          </>
        )}

        <button
          className="bookings-pagination__btn"
          disabled={currentPage === totalPages || isDisabled}
          onClick={() => onPageChange(currentPage + 1)}
          title="Next page"
        >
          ›
        </button>
        <button
          className="bookings-pagination__btn"
          disabled={currentPage === totalPages || isDisabled}
          onClick={() => onPageChange(totalPages)}
          title="Last page"
        >
          »
        </button>
      </div>
    </div>
  );
}

export default function Bookings() {
  const location = useLocation();

  // ── Data state ─────────────────────────────────────────────────────────────
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalDocs, setTotalDocs] = useState(0);

  // ── Pagination ─────────────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);

  // ── Filter / Sort state ────────────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("status") || "All";
  });
  const [paymentFilter, setPaymentFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const [sortKey, setSortKey] = useState("created_on_desc");

  // ── Stats (separate lightweight fetch) ──────────────────────────────
  const [stats, setStats] = useState({ total: 0, pending: 0, confirmed: 0, completed: 0 });

  // ── Detail drawer / modals ─────────────────────────────────────────────────
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [statusModal, setStatusModal] = useState(null);
  const [paymentModal, setPaymentModal] = useState(null);
  const [savingPayment, setSavingPayment] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [tempStatusFilter, setTempStatusFilter] = useState("All");
  const [tempPaymentFilter, setTempPaymentFilter] = useState("All");
  const [tempTypeFilter, setTempTypeFilter] = useState("All");
  const [tempSortKey, setTempSortKey] = useState("created_on_desc");
  const dashboardBookingIdRef = useRef(null);

  // ── Toast ──────────────────────────────────────────────────────────────────
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef();

  const showToast = useCallback((message, type = "success") => {
    clearTimeout(toastTimerRef.current);
    setToast({ message, type });
    toastTimerRef.current = setTimeout(() => setToast(null), 4000);
  }, []);

  // ── Debounce search ────────────────────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1); // reset to page 1 on new search
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // ── Reset page on filter/sort change ──────────────────────────────────────
  const handleStatusFilterChange = (v) => { setStatusFilter(v); setCurrentPage(1); };
  const handlePaymentFilterChange = (v) => { setPaymentFilter(v); setCurrentPage(1); };
  const handleTypeFilterChange = (v) => { setTypeFilter(v); setCurrentPage(1); };
  const handleSortChange = (v) => { setSortKey(v); setCurrentPage(1); };

  // ── Build filter array for Go backend ─────────────────────────────────────
  const buildFilterConditions = useCallback(() => {
    const conditions = [];

    if (debouncedSearch) {
      conditions.push({
        clause: "OR",
        conditions: [
          { column: "client_name", operator: "CONTAINS", type: "string", value: debouncedSearch },
          { column: "client_email", operator: "CONTAINS", type: "string", value: debouncedSearch },
          { column: "client_phone", operator: "CONTAINS", type: "string", value: debouncedSearch },
          { column: "function_type", operator: "CONTAINS", type: "string", value: debouncedSearch },
          { column: "package_name", operator: "CONTAINS", type: "string", value: debouncedSearch },
          { column: "plan_name", operator: "CONTAINS", type: "string", value: debouncedSearch },
          { column: "function_address", operator: "CONTAINS", type: "string", value: debouncedSearch },
        ],
      });
    }

    if (statusFilter !== "All") {
      conditions.push({
        clause: "AND",
        conditions: [
          { column: "status", operator: "EQUALS", type: "string", value: statusFilter },
        ],
      });
    }

    if (typeFilter !== "All") {
      conditions.push({
        clause: "AND",
        conditions: [
          { column: "function_type", operator: "EQUALS", type: "string", value: typeFilter },
        ],
      });
    }

    return conditions;
  }, [debouncedSearch, statusFilter, typeFilter]);


  // ── Build sort array for Go backend ───────────────────────────────────────
  const buildSortCriteria = useCallback(() => {
    const sortMap = {
      created_on_desc: [{ colId: "created_on", sort: "desc" }],
      created_on_asc: [{ colId: "created_on", sort: "asc" }],
      function_date_desc: [{ colId: "function_date", sort: "desc" }],
      function_date_asc: [{ colId: "function_date", sort: "asc" }],
      client_name_asc: [{ colId: "client_name", sort: "asc" }],
      client_name_desc: [{ colId: "client_name", sort: "desc" }],
    };
    return sortMap[sortKey] || sortMap["created_on_desc"];
  }, [sortKey]);

  // ── Main data fetch (server-side paged + filtered) ─────────────────────────
  const fetchBookings = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const start = (page - 1) * PAGE_SIZE;
      const end = page * PAGE_SIZE;

      const filterConditions = buildFilterConditions();
      const sortCriteria = buildSortCriteria();

      const body = {
        start,
        end,
        sort: sortCriteria,
        filter: filterConditions,
        first_filter_append: true,
      };

      const res = await fetch(`${API_BASE}/dataset/data/getbookings`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || data.status !== 200) {
        throw new Error(data.error_msg || "Failed to fetch bookings.");
      }

      const responseArr = data?.data?.[0]?.response ?? [];
      const paginationArr = data?.data?.[0]?.pagination ?? [];
      const total = paginationArr?.[0]?.totalDocs ?? 0;

      // Payment filter is client-side only (derived from paymentsData join)
      let filtered = responseArr;
      if (paymentFilter !== "All") {
        filtered = responseArr.filter((b) => getPaymentStatus(b) === paymentFilter);
      }

      setBookings(filtered);
      // Adjust totalDocs for payment filter (approximate — full count from server)
      setTotalDocs(paymentFilter !== "All" ? filtered.length : total);
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setLoading(false);
    }
  }, [buildFilterConditions, buildSortCriteria, paymentFilter, showToast]);

  // ── Stats fetch via getstatuscount dataset ─────────────────────────────────
  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/dataset/data/getstatuscount`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ start: 0, end: 1 }),
      });
      const data = await res.json();
      if (!res.ok || data.status !== 200) return;
      const row = data?.data?.[0]?.response?.[0] ?? {};
      setStats({
        total: row.totalCount ?? 0,
        pending: row.pendingCount ?? 0,
        confirmed: row.confirmedCount ?? 0,
        completed: row.completedCount ?? 0,
      });
    } catch (_) { /* silently ignore */ }
  }, []);

  // ── Trigger fetch when filters/sort/page change ────────────────────────────
  useEffect(() => {
    fetchBookings(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch, statusFilter, paymentFilter, typeFilter, sortKey]);

  useEffect(() => {
    fetchStats();
    return () => clearTimeout(toastTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Apply ?status= query param from URL (e.g. navigated from Dashboard cards)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusParam = params.get("status");
    if (statusParam) {
      setStatusFilter(statusParam);
    } else {
      setStatusFilter("All");
    }
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // ── Deep-link from dashboard (bookingId in location state) ────────────────
  useEffect(() => {
    const bookingId =
      location.state?.bookingId ||
      new URLSearchParams(location.search).get("bookingId");

    if (!bookingId || dashboardBookingIdRef.current === bookingId) return;

    const booking = bookings.find((b) => b._id === bookingId);
    if (booking) {
      setSelectedBooking(booking);
      dashboardBookingIdRef.current = bookingId;
    } else {
      const fetchSingleBooking = async () => {
        try {
          const res = await fetch(`${API_BASE}/dataset/data/getbookings`, {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify({
              start: 0,
              end: 1,
              filter: [
                {
                  clause: "AND",
                  conditions: [
                    { column: "_id", operator: "EQUALS", type: "string", value: bookingId }
                  ]
                }
              ]
            })
          });
          const data = await res.json();
          if (res.ok && data.status === 200) {
            const fetched = data?.data?.[0]?.response?.[0];
            if (fetched) {
              setBookings((prev) => {
                if (prev.some((b) => b._id === fetched._id)) return prev;
                return [fetched, ...prev];
              });
              setSelectedBooking(fetched);
              dashboardBookingIdRef.current = bookingId;
            }
          }
        } catch (err) {
          console.error("Failed to fetch deep-linked booking via dataset:", err);
        }
      };
      fetchSingleBooking();
    }
  }, [bookings, location.search, location.state]);

  // ── Update Booking Status ─────────────────────────────────────────────────
  const handleUpdateStatus = async (bookingId, newStatus) => {
    const targetBooking = bookings.find((b) => b._id === bookingId) || (selectedBooking?._id === bookingId ? selectedBooking : null);
    if (newStatus === "Completed" && targetBooking?.status !== "Confirmed") {
      showToast("Booking must be Confirmed before it can be Completed.", "error");
      return;
    }

    setUpdatingStatusId(bookingId);
    try {
      const res = await fetch(`${API_BASE}/schedule/bookings/${bookingId}`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error_msg || "Failed to update booking status.");
      }
      showToast(`Booking marked as ${newStatus}.`, "success");
      setBookings((prev) =>
        prev.map((b) => (b._id === bookingId ? { ...b, status: newStatus } : b))
      );
      if (selectedBooking && selectedBooking._id === bookingId) {
        setSelectedBooking((prev) => ({ ...prev, status: newStatus }));
      }
      setStatusModal(null);
      fetchStats();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setUpdatingStatusId(null);
    }
  };

  // ── Update Booking Status with Reason ─────────────────────────────────────
  const handleUpdateStatusWithReason = async (bookingId, newStatus, reason) => {
    setUpdatingStatusId(bookingId);
    try {
      const res = await fetch(`${API_BASE}/schedule/bookings/${bookingId}`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({ status: newStatus, cancel_reason: reason }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error_msg || "Failed to update booking status.");
      }
      showToast(`Booking marked as ${newStatus}.`, "success");
      setBookings((prev) =>
        prev.map((b) =>
          b._id === bookingId ? { ...b, status: newStatus, cancel_reason: reason } : b
        )
      );
      if (selectedBooking && selectedBooking._id === bookingId) {
        setSelectedBooking((prev) => ({ ...prev, status: newStatus, cancel_reason: reason }));
      }
      setStatusModal(null);
      fetchStats();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setUpdatingStatusId(null);
    }
  };

  // ── Payment status change ─────────────────────────────────────────────────
  const handlePaymentStatusChange = (bookingId, targetStatus) => {
    if (targetStatus === "Not Paid") return;
    const booking = bookings.find((b) => b._id === bookingId);
    if (booking?.status !== "Confirmed") {
      showToast("Confirm booking before adding payment.", "error");
      return;
    }
    const totalPrice = Number(booking?.package_price || booking?.amount || (booking?.planeData && booking?.planeData?.price) || 0);
    const alreadyPaid = getTotalPaid(booking);
    let suggestAmount = totalPrice - alreadyPaid;
    if (suggestAmount < 0) suggestAmount = 0;
    if (targetStatus === "Advance Paid") suggestAmount = 0;

    setPaymentModal({
      bookingId,
      targetStatus,
      amount: suggestAmount,
      paymentMethod: "Cash",
      paymentDate: new Date(),
      proofFile: null,
      proofFileName: "",
    });
  };

  // ── File upload ───────────────────────────────────────────────────────────
  const handleFileUpload = async (file, bookingId) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", "payment_proofs");
    fd.append("ref_id", bookingId);

    const res = await fetch(`${API_BASE}/fileupload`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
        OrgId: ORG_ID,
      },
      body: fd,
    });
    const data = await res.json();
    if (!res.ok || data.status !== 200) {
      throw new Error(data.error_msg || "Failed to upload payment proof file.");
    }
    return data?.data?.[0]?.url;
  };

  // ── Save payment details ──────────────────────────────────────────────────
  const handleSavePaymentDetails = async (e) => {
    e.preventDefault();
    const { bookingId, targetStatus, amount, paymentMethod, paymentDate, proofFile } = paymentModal;

    if (!amount || Number(amount) <= 0) {
      showToast("Please enter a valid payment amount.", "error");
      return;
    }
    if (paymentMethod === "Online Payment" && !proofFile) {
      showToast("Please select a proof file for online payment.", "error");
      return;
    }

    const booking = bookings.find((b) => b._id === bookingId);
    const totalPrice = Number(booking?.package_price || booking?.amount || (booking?.planeData && booking?.planeData?.price) || 0);
    const enteredAmount = Number(amount);
    const alreadyPaid = getTotalPaid(booking);

    const remaining = totalPrice - alreadyPaid;

    if (targetStatus === "Advance Paid") {
      if (enteredAmount > remaining) {
        showToast(`Payment amount exceeds the remaining balance of ${formatCurrency(remaining)}.`, "error");
        return;
      }
      if (enteredAmount === remaining) {
        showToast(`This payment of ${formatCurrency(enteredAmount)} clears the remaining balance. Please use "Fully Paid" to record this.`, "error");
        return;
      }
    }

    if (targetStatus === "Fully Paid") {
      if (remaining <= 0) {
        showToast("This booking is already fully paid.", "error");
        return;
      }
      if (enteredAmount > remaining) {
        showToast(`Amount exceeds the remaining balance of ${formatCurrency(remaining)}.`, "error");
        return;
      }
    }

    setSavingPayment(true);
    try {
      let proofUrl = "";
      if (paymentMethod === "Online Payment" && proofFile) {
        proofUrl = await handleFileUpload(proofFile, bookingId);
      }

      const clientName = booking?.client_name || "N/A";
      const clientEmail = booking?.client_email || "N/A";

      let isoPaymentDate = null;
      if (paymentDate) {
        const d = new Date(paymentDate);
        if (!isNaN(d.getTime())) {
          isoPaymentDate = d.toISOString();
        }
      }
      if (!isoPaymentDate) {
        isoPaymentDate = new Date().toISOString();
      }

      const paymentRecord = {
        booking_id: bookingId,
        client_name: clientName,
        client_email: clientEmail,
        amount: enteredAmount,
        payment_method: paymentMethod,
        proof_url: proofUrl,
        payment_status: targetStatus,
        payment_date: isoPaymentDate,
      };

      const resPay = await fetch(`${API_BASE}/entities/payments`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(paymentRecord),
      });
      const dataPay = await resPay.json();
      if (!resPay.ok) {
        throw new Error(dataPay.error_msg || "Failed to save payment details.");
      }

      showToast(`Payment of ${formatCurrency(enteredAmount)} saved.`, "success");

      const newPayment = {
        _id: dataPay?.data?.["insert ID"] || Date.now().toString(),
        ...paymentRecord,
        created_on: isoPaymentDate,
      };
      setBookings((prev) =>
        prev.map((b) => {
          if (b._id !== bookingId) return b;
          const existing = Array.isArray(b.paymentsData) ? b.paymentsData : [];
          return { ...b, paymentsData: [...existing, newPayment] };
        })
      );
      if (selectedBooking && selectedBooking._id === bookingId) {
        const existing = Array.isArray(selectedBooking.paymentsData) ? selectedBooking.paymentsData : [];
        setSelectedBooking((prev) => ({
          ...prev,
          paymentsData: [...existing, newPayment],
        }));
      }

      setPaymentModal(null);
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setSavingPayment(false);
    }
  };

  // ── Derived helpers ───────────────────────────────────────────────────────
  const getPaymentStatus = (booking) => {
    const payments = booking?.paymentsData;
    if (Array.isArray(payments) && payments.length > 0) {
      const sorted = [...payments].sort(
        (a, b) => new Date(b.payment_date || b.created_on || 0) - new Date(a.payment_date || a.created_on || 0)
      );
      return sorted[0].payment_status || "Not Paid";
    }
    return "Not Paid";
  };

  const getTotalPaid = (booking) => {
    const payments = booking?.paymentsData;
    if (!Array.isArray(payments)) return 0;
    return payments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
  };

  const handleTriggerStatus = (bookingId, actionType) => {
    const booking = bookings.find((b) => b._id === bookingId) || (selectedBooking?._id === bookingId ? selectedBooking : null);
    if (!booking) return;

    if (actionType === "confirm" && booking.status !== "Confirmed") {
      setStatusModal({ type: "confirm", bookingId });
    } else if (actionType === "cancel" && booking.status !== "Cancelled") {
      setStatusModal({ type: "cancel", bookingId, reason: "" });
    } else if (actionType === "complete" && booking.status !== "Completed") {
      if (booking.status !== "Confirmed") {
        showToast("Booking must be Confirmed before it can be Completed.", "error");
        return;
      }
      setStatusModal({ type: "complete", bookingId });
    }
  };

  const formatDate = (dateField) => {
    const d = parseMongoDate(dateField);
    if (!d || isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (val) => {
    if (val === undefined || val === null) return "N/A";
    const num = Number(val);
    if (isNaN(num)) return val;
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(num);
  };

  const totalPages = Math.max(1, Math.ceil(totalDocs / PAGE_SIZE));
  const isSelectedFinalState = selectedBooking && (selectedBooking.status === "Cancelled" || selectedBooking.status === "Completed");
  const isSelectedConfirmed = selectedBooking?.status === "Confirmed";

  const hasActiveFilters = debouncedSearch || statusFilter !== "All" || paymentFilter !== "All" || typeFilter !== "All";

  const handleClearFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setStatusFilter("All");
    setPaymentFilter("All");
    setTypeFilter("All");
    setSortKey("created_on_desc");
    setCurrentPage(1);
  };

  return (
    <AdminLayout>
      <div className="bookings-page">
        <header className="bookings-page__header">
          <h2 className="bookings-page__title">Manage Bookings</h2>
          <div className="bookings-page__stats">
            <div className="bookings-page__stat-card">
              <span className="bookings-page__stat-lbl">Total Bookings</span>
              <span className="bookings-page__stat-val">{stats.total}</span>
            </div>
            <div className="bookings-page__stat-card">
              <span className="bookings-page__stat-lbl">Pending</span>
              <span className="bookings-page__stat-val">{stats.pending}</span>
            </div>
            <div className="bookings-page__stat-card">
              <span className="bookings-page__stat-lbl">Confirmed</span>
              <span className="bookings-page__stat-val">{stats.confirmed}</span>
            </div>
            <div className="bookings-page__stat-card">
              <span className="bookings-page__stat-lbl">Completed</span>
              <span className="bookings-page__stat-val">{stats.completed}</span>
            </div>
          </div>
        </header>

        {/* Filters & Search Controls */}
        <section className="booking-filters">

          {/* Search */}
          <div className="booking-filters__field booking-filters__field--search">
            <span className="booking-filters__label">Search</span>
            <div className="booking-search-input-wrapper">
              <svg className="booking-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input
                type="text"
                placeholder="Client name, phone, event type…"
                className="booking-filters__input booking-filters__input--has-icon"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Mobile Filter Button */}
          <div className="booking-filters__mobile-btn-wrap">
            <button
              type="button"
              className={`booking-filters__mobile-btn${hasActiveFilters ? " booking-filters__mobile-btn--active" : ""}`}
              onClick={() => {
                setTempStatusFilter(statusFilter);
                setTempPaymentFilter(paymentFilter);
                setTempTypeFilter(typeFilter);
                setTempSortKey(sortKey);
                setShowFilterModal(true);
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              <span>Filter</span>
              {hasActiveFilters && <span className="booking-filters__mobile-dot" />}
            </button>

            {hasActiveFilters && (
              <button
                type="button"
                className="booking-btn booking-btn--danger"
                onClick={handleClearFilters}
                style={{ height: "36px", padding: "0 0.75rem", fontSize: "0.75rem" }}
              >
                ✕ Clear
              </button>
            )}
          </div>

          {/* Desktop Filter group */}
          <div className="booking-filters__group booking-filters__group--desktop">
            <div className="booking-filters__field booking-filters__field--filter">
              <span className="booking-filters__label">Status</span>
              <FilterDropdown
                label="All Statuses"
                value={statusFilter}
                onChange={handleStatusFilterChange}
                options={[
                  { value: "All", label: "All Statuses" },
                  { value: "Pending", label: "Pending" },
                  { value: "Confirmed", label: "Confirmed" },
                  { value: "Completed", label: "Completed" },
                  { value: "Cancelled", label: "Cancelled" },
                ]}
              />
            </div>

            <div className="booking-filters__field booking-filters__field--filter">
              <span className="booking-filters__label">Payment</span>
              <FilterDropdown
                label="All Payments"
                value={paymentFilter}
                onChange={handlePaymentFilterChange}
                options={[
                  { value: "All", label: "All Payments" },
                  { value: "Not Paid", label: "Not Paid" },
                  { value: "Advance Paid", label: "Advance Paid" },
                  { value: "Fully Paid", label: "Fully Paid" },
                ]}
              />
            </div>

            <div className="booking-filters__field booking-filters__field--filter">
              <span className="booking-filters__label">Function</span>
              <FilterDropdown
                label="All Types"
                value={typeFilter}
                onChange={handleTypeFilterChange}
                options={[
                  { value: "All", label: "All Types" },
                  { value: "Wedding", label: "Wedding" },
                  { value: "Birthday", label: "Birthday" },
                  { value: "Corporate", label: "Corporate" },
                  { value: "Engagement", label: "Engagement" },
                  { value: "Baby Shower", label: "Baby Shower" },
                  { value: "Naming Ceremony", label: "Naming Ceremony" },
                  { value: "Other", label: "Other" },
                ]}
              />
            </div>

            <div className="booking-filters__field booking-filters__field--filter">
              <span className="booking-filters__label">Sort</span>
              <FilterDropdown
                label="Sort Order"
                value={sortKey}
                onChange={handleSortChange}
                options={[
                  { value: "created_on_desc", label: "Newest First" },
                  { value: "created_on_asc", label: "Oldest First" },
                  { value: "function_date_asc", label: "Event (Soonest)" },
                  { value: "function_date_desc", label: "Event (Furthest)" },
                  { value: "client_name_asc", label: "Name A–Z" },
                  { value: "client_name_desc", label: "Name Z–A" },
                ]}
              />
            </div>

            {hasActiveFilters && (
              <button
                type="button"
                className="booking-btn booking-btn--danger"
                onClick={handleClearFilters}
                style={{ alignSelf: "flex-end", height: "36px", padding: "0 1rem" }}
              >
                ✕ Clear
              </button>
            )}
          </div>
        </section>

        {/* Mobile Filter Modal Popup */}
        {showFilterModal && (
          <div className="booking-modal-overlay" onClick={() => setShowFilterModal(false)}>
            <div className="booking-modal booking-modal--filter" onClick={(e) => e.stopPropagation()}>
              <div className="booking-modal__header">
                <h3 className="booking-modal__title">Filter Bookings</h3>
                <button type="button" className="booking-modal__close" onClick={() => setShowFilterModal(false)}>
                  ✕
                </button>
              </div>

              <div className="booking-modal__body" style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
                <div className="booking-filters__field">
                  <span className="booking-filters__label">Status</span>
                  <FilterDropdown
                    label="All Statuses"
                    value={tempStatusFilter}
                    onChange={setTempStatusFilter}
                    options={[
                      { value: "All", label: "All Statuses" },
                      { value: "Pending", label: "Pending" },
                      { value: "Confirmed", label: "Confirmed" },
                      { value: "Completed", label: "Completed" },
                      { value: "Cancelled", label: "Cancelled" },
                    ]}
                  />
                </div>

                <div className="booking-filters__field">
                  <span className="booking-filters__label">Payment Status</span>
                  <FilterDropdown
                    label="All Payments"
                    value={tempPaymentFilter}
                    onChange={setTempPaymentFilter}
                    options={[
                      { value: "All", label: "All Payments" },
                      { value: "Not Paid", label: "Not Paid" },
                      { value: "Advance Paid", label: "Advance Paid" },
                      { value: "Fully Paid", label: "Fully Paid" },
                    ]}
                  />
                </div>

                <div className="booking-filters__field">
                  <span className="booking-filters__label">Function Type</span>
                  <FilterDropdown
                    label="All Types"
                    value={tempTypeFilter}
                    onChange={setTempTypeFilter}
                    options={[
                      { value: "All", label: "All Types" },
                      { value: "Wedding", label: "Wedding" },
                      { value: "Birthday", label: "Birthday" },
                      { value: "Corporate", label: "Corporate" },
                      { value: "Engagement", label: "Engagement" },
                      { value: "Baby Shower", label: "Baby Shower" },
                      { value: "Naming Ceremony", label: "Naming Ceremony" },
                      { value: "Other", label: "Other" },
                    ]}
                  />
                </div>

                <div className="booking-filters__field">
                  <span className="booking-filters__label">Sort Order</span>
                  <FilterDropdown
                    label="Sort Order"
                    value={tempSortKey}
                    onChange={setTempSortKey}
                    options={[
                      { value: "created_on_desc", label: "Newest First" },
                      { value: "created_on_asc", label: "Oldest First" },
                      { value: "function_date_asc", label: "Event (Soonest)" },
                      { value: "function_date_desc", label: "Event (Furthest)" },
                      { value: "client_name_asc", label: "Name A–Z" },
                      { value: "client_name_desc", label: "Name Z–A" },
                    ]}
                  />
                </div>
              </div>

              <div className="booking-modal__footer" style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end", marginTop: "1.5rem" }}>
                <button
                  type="button"
                  className="booking-btn booking-btn--ghost"
                  onClick={() => {
                    setTempStatusFilter("All");
                    setTempPaymentFilter("All");
                    setTempTypeFilter("All");
                    setTempSortKey("created_on_desc");
                  }}
                >
                  Reset
                </button>
                <button
                  type="button"
                  className="booking-btn booking-btn--primary"
                  onClick={() => {
                    setStatusFilter(tempStatusFilter);
                    setPaymentFilter(tempPaymentFilter);
                    setTypeFilter(tempTypeFilter);
                    setSortKey(tempSortKey);
                    setCurrentPage(1);
                    setShowFilterModal(false);
                  }}
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        )}

        <div style={{ position: "relative", minHeight: "200px" }}>
          {loading && (
            <div className="app-loading-overlay">
              <div className="app-spinner"></div>
            </div>
          )}

          {bookings.length === 0 && !loading ? (
            <div className="bookings-empty">
              <span className="bookings-empty__text">No bookings found matching filters.</span>
            </div>
          ) : (
            <div className={`booking-table-wrap ${loading ? "booking-table-wrap--loading" : ""}`}>
              <table className="booking-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Function</th>
                    <th>Package</th>
                    <th>Event Date</th>
                    <th>Status</th>
                    <th>Payment</th>
                    <th className="booking-table__th--action">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b._id} style={{ cursor: "pointer" }} onClick={() => setSelectedBooking(b)}>
                      <td>
                        <div className="booking-client-cell">
                          <span className="booking-client-name">{b.client_name}</span>
                          {b.client_phone && <span className="booking-client-meta">{b.client_phone}</span>}
                          {b.client_email && <span className="booking-client-meta">{b.client_email}</span>}
                        </div>
                      </td>
                      <td>
                        <span style={{ fontSize: "0.82rem" }}>{b.function_type || "—"}</span>
                      </td>
                      <td>
                        <div className="booking-client-cell">
                          <span className="booking-client-name">{b.package_name || b.plan_name || "Custom"}</span>
                          <span className="booking-client-meta">{formatCurrency(b.package_price || b.amount || (b.planeData && b.planeData.price))}</span>
                        </div>
                      </td>
                      <td>
                        <div className="booking-client-cell">
                          <span className="booking-client-name">{formatDate(b.function_date)}</span>
                          {b.function_time && <span className="booking-client-meta">{b.function_time}</span>}
                        </div>
                      </td>
                      <td>
                        <span className={`booking-status booking-status--${(b.status || "pending").toLowerCase()}`}>
                          {b.status || "Pending"}
                        </span>
                      </td>
                      <td>
                        <span className={`booking-status booking-status--${getPaymentStatus(b).toLowerCase().replace(/ /g, "-")}`}>
                          {getPaymentStatus(b)}
                        </span>
                      </td>
                      <td className="booking-table__td--action" onClick={(e) => e.stopPropagation()}>
                        <div className="booking-actions-cell">
                          <ActionDropdown
                            booking={b}
                            paymentStatus={getPaymentStatus(b)}
                            onTriggerStatus={handleTriggerStatus}
                            onUpdatePayment={handlePaymentStatusChange}
                            onViewDetails={() => setSelectedBooking(b)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          totalDocs={totalDocs}
          pageSize={PAGE_SIZE}
          onPageChange={setCurrentPage}
          loading={loading}
        />

      </div>

      {/* Details Side Drawer */}
      {selectedBooking && (() => {
        const initials = (selectedBooking.client_name || "?")
          .split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();

        const bookingStatusKey = (selectedBooking.status || "pending").toLowerCase();
        const paymentStatusKey = getPaymentStatus(selectedBooking).toLowerCase().replace(/ /g, "-");

        return (
          <div className="booking-drawer-overlay" onClick={() => setSelectedBooking(null)}>
            <div className="booking-drawer" onClick={(e) => e.stopPropagation()}>

              {/* ── Hero Header ── */}
              <header className="booking-drawer__header">
                <div className="booking-drawer__header-top">
                  <div className="booking-drawer__header-left">
                    <div className="booking-drawer__avatar">{initials}</div>
                    <div className="booking-drawer__title-area">
                      <h3 className="booking-drawer__title">{selectedBooking.client_name}</h3>
                      <span className="booking-drawer__subtitle">
                        {selectedBooking.function_type} &middot; {formatDate(selectedBooking.function_date)}
                        {selectedBooking.function_time ? ` at ${selectedBooking.function_time}` : ""}
                      </span>
                    </div>
                  </div>
                  <div className="booking-drawer__header-actions">
                    <button
                      className="booking-drawer__close"
                      onClick={() => setSelectedBooking(null)}
                      aria-label="Close"
                    >✕</button>
                  </div>
                </div>

                {/* Status Strip */}
                <div className="booking-drawer__header-status">
                  <div className="booking-drawer__status-pill">
                    <span className="booking-drawer__status-pill-lbl">Booking</span>
                    <span className={`booking-drawer__status-pill-val booking-drawer__status-pill-val--${bookingStatusKey}`}>
                      {selectedBooking.status || "Pending"}
                    </span>
                  </div>
                  <div className="booking-drawer__status-pill">
                    <span className="booking-drawer__status-pill-lbl">Payment</span>
                    <span className={`booking-drawer__status-pill-val booking-drawer__status-pill-val--${paymentStatusKey}`}>
                      {getPaymentStatus(selectedBooking)}
                    </span>
                  </div>
                  <div className="booking-drawer__status-pill">
                    <span className="booking-drawer__status-pill-lbl">Amount</span>
                    <span className="booking-drawer__status-pill-val booking-drawer__status-pill-val--amount">
                      {formatCurrency(selectedBooking.package_price || selectedBooking.amount || (selectedBooking.planeData && selectedBooking.planeData.price))}
                    </span>
                  </div>
                </div>
              </header>

              {/* ── Body ── */}
              <div className="booking-drawer__body">

                {/* Update Statuses */}
                {!isSelectedFinalState && (
                  <div className="booking-drawer__section">
                    <div className="booking-drawer__section-header">
                      <svg className="booking-drawer__section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
                      </svg>
                      <h4 className="booking-drawer__section-title">Update Status</h4>
                    </div>
                    <div className="booking-drawer__section-body">
                      <div className="booking-drawer__ctrl-row">
                        <span className="booking-drawer__ctrl-lbl">Booking Status</span>
                        <div className="booking-drawer__ctrl-input">
                          <FilterDropdown
                            label="Select Status"
                            value={
                              selectedBooking.status === "Confirmed" ? "Confirm" :
                                selectedBooking.status === "Completed" ? "Complete" :
                                  selectedBooking.status === "Cancelled" ? "Cancel" : "Pending"
                            }
                            onChange={(val) => {
                              if (val === "Confirm") handleTriggerStatus(selectedBooking._id, "confirm");
                              else if (val === "Cancel") handleTriggerStatus(selectedBooking._id, "cancel");
                              else if (val === "Complete") handleTriggerStatus(selectedBooking._id, "complete");
                            }}
                            options={[
                              ...(selectedBooking.status === "Pending" ? [{ value: "Pending", label: "Pending" }] : []),
                              ...(selectedBooking.status !== "Confirmed" ? [{ value: "Confirm", label: "Confirm" }] : []),
                              ...(selectedBooking.status === "Confirmed" ? [{ value: "Complete", label: "Complete" }] : []),
                              { value: "Cancel", label: "Cancel" },
                            ]}
                          />
                        </div>
                      </div>

                      {isSelectedConfirmed && getPaymentStatus(selectedBooking) !== "Fully Paid" && (
                        <div className="booking-drawer__ctrl-row">
                          <span className="booking-drawer__ctrl-lbl">Add Payment</span>
                          <div className="booking-drawer__ctrl-input">
                            <FilterDropdown
                              label="Select Payment"
                              value={null}
                              onChange={(val) => handlePaymentStatusChange(selectedBooking._id, val)}
                              options={[
                                { value: "Advance Paid", label: "Advance Paid" },
                                { value: "Fully Paid", label: "Fully Paid" },
                              ]}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Client Information */}
                <div className="booking-drawer__section">
                  <div className="booking-drawer__section-header">
                    <svg className="booking-drawer__section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                    </svg>
                    <h4 className="booking-drawer__section-title">Client Information</h4>
                  </div>
                  <div className="booking-drawer__section-body">
                    <div className="booking-drawer__grid">
                      <div className="booking-detail-item">
                        <span className="booking-detail-label">Full Name</span>
                        <span className="booking-detail-val">{selectedBooking.client_name || "—"}</span>
                      </div>
                      <div className="booking-detail-item">
                        <span className="booking-detail-label">Phone</span>
                        <span className="booking-detail-val">{selectedBooking.client_phone || "—"}</span>
                      </div>
                      <div className="booking-detail-item">
                        <span className="booking-detail-label">Email</span>
                        <span className="booking-detail-val">{selectedBooking.client_email || "—"}</span>
                      </div>
                      <div className="booking-detail-item">
                        <span className="booking-detail-label">Booked On</span>
                        <span className="booking-detail-val">{formatDate(selectedBooking.created_on)}</span>
                      </div>
                    </div>
                    {selectedBooking.client_address && (
                      <div className="booking-detail-item">
                        <span className="booking-detail-label">Billing Address</span>
                        <span className="booking-detail-val">{selectedBooking.client_address}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Event Details */}
                <div className="booking-drawer__section">
                  <div className="booking-drawer__section-header">
                    <svg className="booking-drawer__section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    <h4 className="booking-drawer__section-title">Event &amp; Service Details</h4>
                  </div>
                  <div className="booking-drawer__section-body">
                    <div className="booking-drawer__grid">
                      <div className="booking-detail-item">
                        <span className="booking-detail-label">Function Type</span>
                        <span className="booking-detail-val">{selectedBooking.function_type || "—"}</span>
                      </div>
                      <div className="booking-detail-item">
                        <span className="booking-detail-label">Event Date &amp; Time</span>
                        <span className="booking-detail-val">
                          {formatDate(selectedBooking.function_date)}
                          {selectedBooking.function_time ? ` · ${selectedBooking.function_time}` : ""}
                        </span>
                      </div>
                      <div className="booking-detail-item">
                        <span className="booking-detail-label">Selected Plan</span>
                        <span className="booking-detail-val">{selectedBooking.package_name || selectedBooking.plan_name || "Custom Plan"}</span>
                      </div>
                      <div className="booking-detail-item">
                        <span className="booking-detail-label">Plan Price</span>
                        <span className="booking-detail-val booking-detail-val--price">
                          {formatCurrency(selectedBooking.package_price || selectedBooking.amount || (selectedBooking.planeData && selectedBooking.planeData.price))}
                        </span>
                      </div>
                    </div>

                    {selectedBooking.function_address && (
                      <div className="booking-detail-item">
                        <span className="booking-detail-label">Venue Address</span>
                        <span className="booking-detail-val">{selectedBooking.function_address}</span>
                      </div>
                    )}

                    {selectedBooking.status === "Cancelled" && selectedBooking.cancel_reason && (
                      <div className="booking-drawer__cancel-note">
                        <span className="booking-drawer__cancel-note-lbl">Cancellation Reason</span>
                        <p className="booking-drawer__cancel-note-text">{selectedBooking.cancel_reason}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Payment History */}
                {selectedBooking.status !== "Pending" && (
                  <div className="booking-drawer__section">
                    <div className="booking-drawer__section-header">
                      <svg className="booking-drawer__section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" />
                      </svg>
                      <h4 className="booking-drawer__section-title">Payment History</h4>
                    </div>
                    <div className="booking-drawer__section-body">
                      {Array.isArray(selectedBooking.paymentsData) && selectedBooking.paymentsData.length > 0 ? (
                        [...selectedBooking.paymentsData]
                          .sort((a, b) => new Date(a.payment_date || a.created_on || 0) - new Date(b.payment_date || b.created_on || 0))
                          .map((pay, idx) => (
                            <div key={pay._id || idx} className="booking-pay-row">
                              <div className="booking-pay-row__left">
                                <span className="booking-pay-row__amount">{formatCurrency(pay.amount)}</span>
                                <span className="booking-pay-row__meta">
                                  {pay.payment_method}
                                  {(pay.payment_date || pay.created_on) ? ` · ${new Date(pay.payment_date || pay.created_on).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}` : ""}
                                </span>
                                {pay.proof_url && (
                                  /\.(jpe?g|png|gif|webp|bmp|svg)(\?|$)/i.test(pay.proof_url) ? (
                                    <a href={pay.proof_url} target="_blank" rel="noopener noreferrer" style={{ marginTop: "0.4rem" }}>
                                      <img
                                        src={pay.proof_url}
                                        alt="Payment proof"
                                        style={{ maxWidth: "100%", objectFit: "contain", border: "1px solid var(--color-border)", borderRadius: "4px", display: "block" }}
                                      />
                                      <span style={{ fontSize: "0.65rem", color: "var(--color-accent)", textDecoration: "underline", marginTop: "0.2rem", display: "inline-block" }}>Open full image</span>
                                    </a>
                                  ) : (
                                    <a href={pay.proof_url} target="_blank" rel="noopener noreferrer"
                                      style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", fontSize: "0.72rem", color: "var(--color-accent)", textDecoration: "underline", marginTop: "0.3rem" }}>
                                      View Proof Document
                                    </a>
                                  )
                                )}
                              </div>
                              <span className={`booking-status booking-status--${(pay.payment_status || "").toLowerCase().replace(/ /g, "-")}`} style={{ fontSize: "0.6rem", flexShrink: 0 }}>
                                {pay.payment_status}
                              </span>
                            </div>
                          ))
                      ) : (
                        <p style={{ margin: "0 0 1rem 0", fontSize: "0.8rem", color: "var(--color-text-muted)", fontStyle: "italic" }}>
                          No payments recorded yet.
                        </p>
                      )}

                      {/* Payment Summary */}
                      <div className="booking-pay-summary" style={{
                        marginTop: "1.25rem",
                        padding: "1rem 1.25rem",
                        background: "var(--color-background)",
                        border: "1px solid var(--color-border)",
                        borderRadius: "6px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.6rem"
                      }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)" }}>Total Contract</span>
                          <span style={{ fontSize: "0.85rem", fontWeight: "600", color: "var(--color-text)" }}>
                            {formatCurrency(selectedBooking.package_price || selectedBooking.amount || (selectedBooking.planeData && selectedBooking.planeData.price))}
                          </span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)" }}>Total Paid</span>
                          <span style={{ fontSize: "0.85rem", fontWeight: "600", color: "#43824f" }}>
                            {formatCurrency(getTotalPaid(selectedBooking))}
                          </span>
                        </div>

                        <div style={{ margin: "0.25rem 0", borderTop: "1px dashed var(--color-border)" }} />

                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontSize: "0.72rem", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text)" }}>Outstanding Balance</span>
                          <span style={{
                            fontSize: "1.15rem",
                            fontWeight: "700",
                            fontFamily: "var(--font-body)",
                            color: (selectedBooking.package_price || selectedBooking.amount || (selectedBooking.planeData && selectedBooking.planeData.price)) - getTotalPaid(selectedBooking) > 0 ? "var(--color-accent)" : "#43824f"
                          }}>
                            {formatCurrency(Math.max(0, (selectedBooking.package_price || selectedBooking.amount || (selectedBooking.planeData && selectedBooking.planeData.price)) - getTotalPaid(selectedBooking)))}
                          </span>
                        </div>
                      </div>

                    </div>
                  </div>
                )}

              </div>{/* end body */}

              <footer className="booking-drawer__footer">
                <button className="booking-btn" onClick={() => setSelectedBooking(null)}>
                  Close
                </button>
              </footer>

            </div>
          </div>
        );
      })()}

      {/* Confirm / Complete / Cancel Status Popup Modals */}
      {statusModal && (
        <div className="booking-modal-overlay" onClick={() => setStatusModal(null)}>
          <div className="booking-modal" style={{ maxWidth: "420px" }} onClick={(e) => e.stopPropagation()}>
            <header className="booking-modal__header">
              <h3 className="booking-modal__title">
                {statusModal.type === "confirm" && "Confirm Booking"}
                {statusModal.type === "complete" && "Complete Booking"}
                {statusModal.type === "cancel" && "Cancel Booking"}
              </h3>
              <button
                className="booking-modal__close"
                onClick={() => setStatusModal(null)}
              >
                ✕
              </button>
            </header>

            <div className="booking-modal__body">
              {statusModal.type === "confirm" && (
                <p style={{ margin: 0, fontSize: "0.95rem", lineHeight: 1.5 }}>
                  Are you sure you want to confirm this booking? This will update the status to <strong>Confirmed</strong>.
                </p>
              )}
              {statusModal.type === "complete" && (
                <p style={{ margin: 0, fontSize: "0.95rem", lineHeight: 1.5 }}>
                  Are you sure you want to mark this booking as <strong>Completed</strong>?
                </p>
              )}
              {statusModal.type === "cancel" && (
                <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                  <p style={{ margin: 0, fontSize: "0.95rem", lineHeight: 1.5 }}>
                    Please enter the reason for cancelling this booking:
                  </p>
                  <textarea
                    rows={4}
                    className="booking-filters__input"
                    placeholder="Enter reason here..."
                    style={{
                      width: "100%",
                      height: "100px",
                      padding: "0.6rem 0.8rem",
                      border: "1px solid var(--color-border)",
                      background: "var(--color-background)",
                      fontFamily: "var(--font-body)",
                      fontSize: "0.875rem",
                      outline: "none",
                      resize: "none"
                    }}
                    value={statusModal.reason || ""}
                    onChange={(e) => setStatusModal({ ...statusModal, reason: e.target.value })}
                  />
                </div>
              )}
            </div>

            <footer className="booking-modal__footer">
              <button
                className="booking-btn"
                onClick={() => setStatusModal(null)}
              >
                Go Back
              </button>
              {statusModal.type === "confirm" && (
                <button
                  className="booking-btn"
                  style={{ background: "var(--color-text)", color: "var(--color-secondary)" }}
                  onClick={() => handleUpdateStatus(statusModal.bookingId, "Confirmed")}
                >
                  Yes, Confirm
                </button>
              )}
              {statusModal.type === "complete" && (
                <button
                  className="booking-btn"
                  style={{ background: "var(--color-text)", color: "var(--color-secondary)" }}
                  onClick={() => handleUpdateStatus(statusModal.bookingId, "Completed")}
                >
                  Yes, Complete
                </button>
              )}
              {statusModal.type === "cancel" && (
                <button
                  className="booking-btn booking-btn--danger"
                  disabled={!statusModal.reason || !statusModal.reason.trim()}
                  onClick={() => handleUpdateStatusWithReason(statusModal.bookingId, "Cancelled", statusModal.reason)}
                >
                  Cancel Booking
                </button>
              )}
            </footer>
          </div>
        </div>
      )}

      {/* Payment Details Modal */}
      {paymentModal && (
        <div className="booking-modal-overlay" onClick={() => !savingPayment && setPaymentModal(null)}>
          <div className="booking-modal" style={{ maxWidth: "460px" }} onClick={(e) => e.stopPropagation()}>
            <header className="booking-modal__header">
              <h3 className="booking-modal__title">Record Payment Details</h3>
              <button
                className="booking-modal__close"
                disabled={savingPayment}
                onClick={() => setPaymentModal(null)}
              >
                ✕
              </button>
            </header>

            <form onSubmit={handleSavePaymentDetails}>
              <div className="booking-modal__body">
                <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
                  <p style={{ margin: 0, fontSize: "0.9rem", color: "var(--color-text-secondary)", lineHeight: 1.4 }}>
                    Enter payment details to update status to <strong>{paymentModal.targetStatus}</strong>.
                  </p>

                  {/* Amount Field */}
                  <div className="payment-form-group">
                    <label className="payment-form-label">Payment Amount (INR)</label>
                    <input
                      type="text"
                      required
                      inputMode="numeric"
                      disabled={savingPayment}
                      className="booking-filters__input"
                      style={{ width: "100%", padding: "0.75rem", fontSize: "0.95rem" }}
                      value={
                        paymentModal.amount !== "" && paymentModal.amount !== undefined
                          ? Number(paymentModal.amount).toLocaleString("en-IN")
                          : ""
                      }
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^0-9]/g, "");
                        setPaymentModal({ ...paymentModal, amount: raw === "" ? "" : Number(raw) });
                      }}
                    />
                  </div>

                  {/* Payment Date Field */}
                  <div className="payment-form-group">
                    <label className="payment-form-label">Payment Date</label>
                    <DatePicker
                      selected={paymentModal.paymentDate}
                      onChange={(date) => {
                        setPaymentModal({ ...paymentModal, paymentDate: date });
                      }}
                      dateFormat="dd MMM yyyy"
                      placeholderText="Select date"
                      maxDate={new Date()}
                      disabled={savingPayment}
                      className="booking-filters__input"
                      wrapperClassName="bk__datepicker-wrap"
                      autoComplete="off"
                    />
                  </div>

                  {/* Payment Method Selector */}
                  <div className="payment-form-group">
                    <label className="payment-form-label">Payment Method</label>
                    <div className="payment-method-selector">
                      <button
                        type="button"
                        disabled={savingPayment}
                        className={`payment-method-btn ${paymentModal.paymentMethod === "Cash" ? "active" : ""}`}
                        onClick={() => setPaymentModal({ ...paymentModal, paymentMethod: "Cash" })}
                      >
                        Cash
                      </button>
                      <button
                        type="button"
                        disabled={savingPayment}
                        className={`payment-method-btn ${paymentModal.paymentMethod === "Online Payment" ? "active" : ""}`}
                        onClick={() => setPaymentModal({ ...paymentModal, paymentMethod: "Online Payment" })}
                      >
                        Online Payment
                      </button>
                    </div>
                  </div>

                  {/* Upload Payment Proof (Online Only) */}
                  {paymentModal.paymentMethod === "Online Payment" && (
                    <div className="payment-form-group">
                      <label className="payment-form-label">Upload Payment Proof</label>
                      <div
                        className={`payment-upload-zone ${paymentModal.proofFile ? "has-file" : ""}`}
                        onDragOver={(e) => {
                          e.preventDefault();
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          if (savingPayment) return;
                          const file = e.dataTransfer.files?.[0];
                          if (file) {
                            setPaymentModal({
                              ...paymentModal,
                              proofFile: file,
                              proofFileName: file.name
                            });
                          }
                        }}
                      >
                        <input
                          type="file"
                          id="payment-proof-file"
                          disabled={savingPayment}
                          style={{ display: "none" }}
                          accept="image/*,application/pdf"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setPaymentModal({
                                ...paymentModal,
                                proofFile: file,
                                proofFileName: file.name
                              });
                            }
                          }}
                        />
                        <label htmlFor="payment-proof-file" className="payment-upload-inner" style={{ cursor: "pointer" }}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24" style={{ color: "var(--color-text-muted)", flexShrink: 0 }}>
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="17 8 12 3 7 8" />
                            <line x1="12" y1="3" x2="12" y2="15" />
                          </svg>
                          {paymentModal.proofFileName ? (
                            <span className="upload-text text-success">{paymentModal.proofFileName}</span>
                          ) : (
                            <span className="upload-text">Click or drag image / PDF to upload proof</span>
                          )}
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <footer className="booking-modal__footer">
                <button
                  type="button"
                  className="booking-btn"
                  disabled={savingPayment}
                  onClick={() => setPaymentModal(null)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="booking-btn"
                  style={{ background: "var(--color-text)", color: "var(--color-secondary)", display: "flex", alignItems: "center", gap: "0.5rem" }}
                  disabled={savingPayment}
                >
                  {savingPayment ? (
                    <>
                      <span className="bookings-loading__spinner" style={{ width: "14px", height: "14px", border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", margin: 0 }}></span>
                      Saving...
                    </>
                  ) : (
                    "Save Payment"
                  )}
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}

      {/* Toast Alert */}
      {toast && (
        <div className="bookings-toast-container">
          <div className={`bookings-toast bookings-toast--${toast.type}`}>
            {toast.type === "success" ? "✓" : "✕"} {toast.message}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
